import axios from 'src/utils/axios';

export const SET_ACTIVITY_COUNTS = '@activity/set-counts';

export const ACTIVITY_AREA = {
  SMART_FORM_ENTRIES: 'smartFormEntries',
  // CON-2026 (P1d)
  ORDERS: 'orders',
  PARTY_GUESTS: 'partyGuests',
  GROUP_MEMBERS: 'groupMembers',
};

// CON-2036 — the two channels a Notifications setting can turn off.
export const ACTIVITY_CHANNEL = {
  BADGE: 'badge',
  PUSH: 'push',
};

// CON-2036 — the Notifications tab's rows, in sidebar order, and the noun a
// row's mute tooltip uses. Orders have no per-item mute.
// CON-2046: newItemsLabel is the "New events / New groups: Notify me /
// Start muted" choice under that row; areas without it have none.
export const ACTIVITY_AREA_SETTINGS = [
  { area: 'smartFormEntries', label: 'Smart Forms & Connect Pages', noun: 'form' },
  { area: 'orders', label: 'Order Follow-Up', noun: null },
  {
    area: 'partyGuests', label: 'Events', noun: 'event', newItemsLabel: 'New events',
  },
  {
    area: 'groupMembers', label: 'Groups', noun: 'group', newItemsLabel: 'New groups',
  },
];

// Order Follow-Up opened with ?new=1 starts on its "Unseen (N)" filter (the
// sidebar link carries it while there are new orders).
export const ORDERS_NEW_FILTER_PARAM = 'new';

/**
 * shouldMarkListSeen — CON-2026 (P1d). A list or tab marks seen through its
 * serverNow only when every unseen item is on the page being shown: the
 * server's unseenTotal counts the whole area (orders) or the whole event or
 * group, and newIds are this page's new rows. Anything on another page, or
 * hidden by a filter, keeps the badge.
 */
export const shouldMarkListSeen = (activity) => !!activity?.serverNow
  && activity.unseenTotal > 0
  && Array.isArray(activity.newIds)
  && activity.newIds.length >= activity.unseenTotal;

/**
 * setActivityCounts — CON-XXXX. Stores { serverNow, areas } (and, since
 * CON-2036, permittedAreas, canPush and preferences when present) from the
 * receive:activity:counts socket event or a REST answer. Only users holding
 * ACTIVITY_BADGES ever receive one, so for everyone else the state stays
 * empty and every badge stays hidden.
 */
export const setActivityCounts = (payload) => ({
  type: SET_ACTIVITY_COUNTS,
  payload,
});

/**
 * fetchActivityCounts — CON-XXXX. GET /api/activity/counts. Callers gate on
 * ACTIVITY_BADGES before dispatching; a failure leaves the counts as they
 * were.
 */
export const fetchActivityCounts = () => async (dispatch) => {
  try {
    const response = await axios.get('/api/activity/counts');
    dispatch(setActivityCounts(response?.data));
  } catch (e) {
    // Badges are best-effort; the next socket update corrects them.
  }
};

/**
 * markActivitySeen — CON-XXXX. POST /api/activity/seen. entityId 0 marks
 * the whole area ("Mark all as seen"). seenThrough is the serverNow the
 * user was shown, never the browser clock, so anything that arrived after
 * the screen loaded stays new. The answer carries fresh counts. A Sign In As
 * session gets { skipped: 'SIGN_IN_AS' } and the counts are unchanged.
 */
export const markActivitySeen = ({ area, entityId = 0, seenThrough }) => async (dispatch) => {
  if (!area || !seenThrough) return;
  try {
    const response = await axios.post('/api/activity/seen', { area, entityId, seenThrough });
    dispatch(setActivityCounts(response?.data));
  } catch (e) {
    // A failed mark just leaves the badge showing.
  }
};

/**
 * setActivityAreaPreference — CON-2036. PUT /api/activity/preferences/area.
 * Turns one area's badges or push on or off. The answer carries fresh counts
 * and settings (and goes to the user's other open apps by socket). A Sign In
 * As session gets { skipped: 'SIGN_IN_AS' } and nothing changes. Resolves to
 * the answer, or null on failure.
 */
export const setActivityAreaPreference = ({ area, channel, enabled }) => async (dispatch) => {
  try {
    const response = await axios.put('/api/activity/preferences/area', { area, channel, enabled });
    dispatch(setActivityCounts(response?.data));
    return response?.data || null;
  } catch (e) {
    return null;
  }
};

/**
 * setActivityMuted — CON-2036. PUT /api/activity/preferences/mute. Mutes or
 * unmutes one or more forms, events or groups (a row's bell, or the Events
 * and Groups bulk Mute / Unmute). A muted item adds nothing to badge counts
 * and sends no push. Resolves to the answer, or null on failure.
 */
export const setActivityMuted = ({ area, entityIds, muted }) => async (dispatch) => {
  if (!area || !Array.isArray(entityIds) || !entityIds.length) return null;
  try {
    const response = await axios.put('/api/activity/preferences/mute', { area, entityIds, muted });
    dispatch(setActivityCounts(response?.data));
    return response?.data || null;
  } catch (e) {
    return null;
  }
};

/**
 * setActivityNewItems — CON-2046. PUT /api/activity/preferences/new-items.
 * Events or Groups: Start muted (startMuted true) makes every event or group
 * created from now on start muted until its bell is turned on; Notify me
 * (false) ends that, and items it already muted stay muted. The answer
 * carries fresh counts and settings. A Sign In As session gets
 * { skipped: 'SIGN_IN_AS' } and nothing changes. Resolves to the answer, or
 * null on failure.
 */
export const setActivityNewItems = ({ area, startMuted }) => async (dispatch) => {
  try {
    const response = await axios.put('/api/activity/preferences/new-items', { area, startMuted });
    dispatch(setActivityCounts(response?.data));
    return response?.data || null;
  } catch (e) {
    return null;
  }
};
